import React, {useContext, useEffect, useRef, useState} from "react";
import "./InstalledApp.css";
import {Link} from "react-router-dom";
import {FormattedMessage, useIntl} from "react-intl";
import {AppLocationState, InstalledAppResponse} from "../../../logic/APITypes";
import ImageWrapper from "../../../components/Image";
import API from "../../../logic/API";
import {KeycloakContext} from "../../contextProviders/KeycloakProvider";
import {SnackbarContext} from "../../../utility/SnackbarProvider";

type InstalledAppProps = InstalledAppResponse & AppLocationState;

/** A tile element of the apps list containing overview information of installed applications */
function InstalledApp({
  isGroupAdmin,
  from,
  id,
  name,
  logo,
  version,
  group_id,
}: InstalledAppProps) {
  const {formatMessage} = useIntl();
  const [updatedVersion, setUpdatedVersion] = useState(version);

  return (
    <div id={`installed_${name}`} className="installedApp">
      <div className="installedApp__link">
        <ImageWrapper
          src={logo}
          width={48}
          height={48}
          alt={formatMessage({id: "general.logo.alt"}, {name: name})}
        />
        <p className="installedApp__name">{name}</p>
      </div>
      <Status
        groupId={group_id}
        appId={id}
        version={updatedVersion}
        name={name}
        isGroupAdmin={isGroupAdmin}
        onUpdateFinish={() => setUpdatedVersion("current")}
      />
      <Link
        id={`details_${name}_link`}
        className="linkButton"
        to={`/groups/${group_id}/apps/installed/${id}`}
        state={{from, isGroupAdmin}}
        aria-label={formatMessage(
          {id: "general.link.ariaLabel.internal"},
          {name: name},
        )}
      >
        <FormattedMessage id="installedApp.details" />
      </Link>
    </div>
  );
}

/** The status component indicating whether the app is up to date or not */
function Status({
  groupId,
  appId,
  version,
  name,
  isGroupAdmin,
  onUpdateFinish,
}: {
  groupId: string;
  appId: string;
  version: "current" | "update";
  name: string;
  isGroupAdmin: boolean | undefined;
  onUpdateFinish: () => void;
}) {
  const [waitingForResponse, setWaitingForResponse] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const {isSchAdmin} = useContext(KeycloakContext);
  const {addSnackbar} = useContext(SnackbarContext);
  const intl = useIntl();
  const refreshRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (isUpdating)
      refreshRef.current = setInterval(() => {
        API.getInstalledApps(groupId).then((response) => {
          if (
            response.payload?.some(
              (app) => app.id === appId && app.version === "current",
            )
          ) {
            clearInterval(refreshRef.current);
            refreshRef.current = undefined;
            onUpdateFinish();
            setIsUpdating(false);
          }
        });
      }, 3000);
  }, [isUpdating]);

  useEffect(
    () => () => {
      if (refreshRef.current) clearInterval(refreshRef.current);
    },
    [],
  );

  return (
    <div className="status">
      <span
        className={`status__circle status__circle--${isUpdating ? "updating" : version}`}
      />
      {isUpdating ? (
        <FormattedMessage id="installedApp.status.updating" />
      ) : version === "current" ? (
        <FormattedMessage id="installedApp.status.current" />
      ) : (
        <FormattedMessage id="installedApp.status.updateAvailable" />
      )}
      {version === "update" && (isSchAdmin || isGroupAdmin) && (
        <button
          id={`update_${name}_button`}
          className="linkButton"
          disabled={waitingForResponse || isUpdating}
          onClick={() => {
            setWaitingForResponse(true);
            API.updateApp(groupId, appId).then((response) => {
              if (response.success) {
                setIsUpdating(true);
              } else
                addSnackbar({
                  variant: "close",
                  text: intl.formatMessage({
                    id: "installedApp.snackbar.update.error",
                  }),
                  hasCloseButton: true,
                });
              setWaitingForResponse(false);
            });
          }}
        >
          <FormattedMessage id="installedApp.status.update" />
        </button>
      )}
    </div>
  );
}

export default InstalledApp;
